import { useLayoutEffect, useState, type CSSProperties, type HTMLAttributes, type RefObject } from 'react';

const GAP = 4;
const MAX_HEIGHT = 320;

/**
 * Places a popup list `position: fixed` under `anchor` (PL12, PL14), so a scrolling container cannot clip it: at least
 * as wide as the anchor, kept inside the viewport, lined up with the anchor's start or (`align: 'end'`) its end. Opens
 * upward when its content does not fit below and there is more room above. Follows scroll and resize, and re-measures
 * as its content changes (filtering). Returns the ref callback for the list, its placement and its inline style.
 */
export function useFloatingPlacement(
  anchor: RefObject<HTMLElement | null>,
  align: 'start' | 'end' = 'start'
) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!el) return;
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      if (!a) return;
      // The content's own height, not the capped one: a list squeezed below must still flip.
      const wanted = Math.min(MAX_HEIGHT, el.scrollHeight);
      const below = window.innerHeight - a.bottom - GAP * 2;
      const above = a.top - GAP * 2;
      const up = wanted > below && above > below;
      const width = Math.max(a.width, el.offsetWidth);
      const wantedLeft = align === 'end' ? a.right - width : a.left;
      const left = Math.max(GAP, Math.min(wantedLeft, window.innerWidth - width - GAP));
      setPlacement(up ? 'top' : 'bottom');
      setStyle(
        up
          ? {
              position: 'fixed',
              left,
              bottom: window.innerHeight - a.top + GAP,
              minWidth: a.width,
              maxHeight: Math.min(MAX_HEIGHT, above),
            }
          : {
              position: 'fixed',
              left,
              top: a.bottom + GAP,
              minWidth: a.width,
              maxHeight: Math.min(MAX_HEIGHT, below),
            }
      );
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null;
    observer?.observe(el);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      observer?.disconnect();
    };
  }, [anchor, el, align]);

  return { ref: setEl, placement, style };
}

/**
 * A combobox's option list that a scrolling container (a dialog body) cannot clip (PL12), placed by
 * `useFloatingPlacement`. It stays in the field's DOM rather than a portal — `fixed` is not clipped by an ancestor's
 * overflow, and inside a modal dialog a list portalled out of it would be out of reach of assistive tech. (An ancestor
 * with a transform would re-anchor it; dialogs and drawers carry none at rest.) `data-placement` says which way it
 * opened.
 */
export function FloatingList({
  anchor,
  className,
  style,
  children,
  ...rest
}: { anchor: RefObject<HTMLElement | null> } & HTMLAttributes<HTMLDivElement>) {
  const { ref, placement, style: pos } = useFloatingPlacement(anchor);
  return (
    <div
      ref={ref}
      className={['nyk-ui-combo-list', 'nyk-ui-combo-list-floating', className].filter(Boolean).join(' ')}
      data-placement={placement}
      style={{ ...style, ...pos }}
      {...rest}
    >
      {children}
    </div>
  );
}
